/**
 * Color Utilities for Chart Differentiation
 *
 * Provides a consistent 12-color palette for differentiating links
 * in folder analytics charts. Colors are chosen for:
 * - High contrast against each other
 * - Accessibility (colorblind-friendly where possible)
 * - Good visibility on both light and dark backgrounds
 */

/**
 * 12-color palette for chart segments
 * Ordered for maximum visual distinction between adjacent colors
 */
export const CHART_PALETTE = [
  '#f97316', // Orange (primary accent)
  '#3b82f6', // Blue
  '#22c55e', // Green
  '#a855f7', // Purple
  '#f43f5e', // Rose
  '#06b6d4', // Cyan
  '#eab308', // Yellow
  '#6366f1', // Indigo
  '#ec4899', // Pink
  '#14b8a6', // Teal
  '#8b5cf6', // Violet
  '#84cc16', // Lime
] as const;

/**
 * Get a color from the palette by index
 * Wraps around if index exceeds palette length
 */
export function getLinkColor(index: number): string {
  const color = CHART_PALETTE[index % CHART_PALETTE.length];
  return color ?? CHART_PALETTE[0];
}

/**
 * Get a color from the palette as HSL for opacity manipulation
 * Returns the hex color string (charts handle opacity separately)
 */
export function getLinkColorWithOpacity(index: number, _opacity: number = 1): string {
  // For now just return the color - Recharts handles opacity via fillOpacity
  return getLinkColor(index);
}

/**
 * Generate an array of colors for a given number of links
 * Useful for legend rendering
 */
export function generatePaletteForLinks(count: number): string[] {
  return Array.from({ length: count }, (_, i) => getLinkColor(i));
}

/**
 * Create a contrasting text color (black or white) based on background
 * Uses luminance calculation for accessibility
 */
export function getContrastTextColor(hexColor: string): 'black' | 'white' {
  // Remove # if present
  const hex = hexColor.replace('#', '');

  // Parse RGB
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  // Calculate relative luminance (WCAG formula)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  return luminance > 0.5 ? 'black' : 'white';
}

/**
 * Lighten a hex color by a percentage
 * Useful for hover states on chart segments
 */
export function lightenColor(hexColor: string, percent: number): string {
  const hex = hexColor.replace('#', '');

  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  const lighten = (value: number) =>
    Math.min(255, Math.round(value + (255 - value) * (percent / 100)));

  const newR = lighten(r).toString(16).padStart(2, '0');
  const newG = lighten(g).toString(16).padStart(2, '0');
  const newB = lighten(b).toString(16).padStart(2, '0');

  return `#${newR}${newG}${newB}`;
}

/**
 * Darken a hex color by a percentage
 * Useful for border colors on chart segments
 */
export function darkenColor(hexColor: string, percent: number): string {
  const hex = hexColor.replace('#', '');

  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  const darken = (value: number) => Math.max(0, Math.round(value * (1 - percent / 100)));

  const newR = darken(r).toString(16).padStart(2, '0');
  const newG = darken(g).toString(16).padStart(2, '0');
  const newB = darken(b).toString(16).padStart(2, '0');

  return `#${newR}${newG}${newB}`;
}

/**
 * Standard chart colors using CSS variables
 * Use these for consistent chart theming across the app
 */
export const CHART_COLORS = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
] as const;

/**
 * Get a chart color by index (cycles through palette)
 * Use this instead of hardcoding colors in individual charts
 */
export function getChartColor(index: number): string {
  return CHART_COLORS[index % CHART_COLORS.length] ?? 'var(--chart-1)';
}

// =============================================================================
// Theme Color Derivation
// =============================================================================

/**
 * Derive a full color palette from a single primary/accent color
 * Used by the simplified Style Step to reduce user choices from 4 colors to 1
 *
 * @param primaryColor - The user's chosen accent color (hex)
 * @param mode - Light or dark mode
 * @returns A complete color palette derived from the primary color
 */
export function derivePaletteFromPrimary(
  primaryColor: string,
  mode: 'light' | 'dark'
): {
  backgroundColor: string;
  accentColor: string;
  textColor: string;
  headingColor: string;
} {
  if (mode === 'light') {
    return {
      backgroundColor: '#ffffff',
      accentColor: primaryColor,
      textColor: '#374151', // gray-700
      headingColor: darkenColor(primaryColor, 40), // Darker version of accent for headings
    };
  } else {
    return {
      backgroundColor: '#0f172a', // slate-900
      accentColor: primaryColor,
      textColor: '#e2e8f0', // slate-200
      headingColor: lightenColor(primaryColor, 30), // Lighter version of accent for headings
    };
  }
}

/**
 * Detect if a color is more suited for light or dark mode based on luminance
 * Useful for auto-detecting the best mode for a given primary color
 */
export function suggestModeForColor(hexColor: string): 'light' | 'dark' {
  const contrast = getContrastTextColor(hexColor);
  // If the color needs white text, it's dark enough that light mode looks better
  // If the color needs black text, it's light enough that dark mode provides good contrast
  return contrast === 'white' ? 'light' : 'light'; // Default to light mode for most cases
}

/**
 * Pre-defined primary color options for quick selection
 * These are curated to work well with both light and dark modes
 */
export const PRIMARY_COLOR_PRESETS = [
  { value: '#3b82f6', label: 'Blue', preview: 'from-blue-500 to-indigo-500' },
  { value: '#10b981', label: 'Emerald', preview: 'from-emerald-500 to-teal-500' },
  { value: '#8b5cf6', label: 'Violet', preview: 'from-violet-500 to-purple-500' },
  { value: '#f59e0b', label: 'Amber', preview: 'from-amber-500 to-orange-500' },
  { value: '#ec4899', label: 'Pink', preview: 'from-pink-500 to-rose-500' },
  { value: '#06b6d4', label: 'Cyan', preview: 'from-cyan-500 to-blue-500' },
  { value: '#ef4444', label: 'Red', preview: 'from-red-500 to-rose-500' },
  { value: '#84cc16', label: 'Lime', preview: 'from-lime-500 to-green-500' },
] as const;
